import styled from '@emotion/styled'
import { Box, Modal, Typography } from '@mui/material'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import TrainingSessionBasicFields from '../../components/coach/TrainingSessionBasicFields'
import TrainingSessionStepsList from '../../components/coach/TrainingSessionStepsList'
import UserContext from '../../context/UserContext'
import useTrainingSessionForm from '../../hooks/useTrainingSessionForm'
import { useTrainingSessionMutations } from '../../hooks/useTrainingSessionMutations'
import { apiClient } from '../../services/apiClient'
import { Exercise } from '../../types/exercise.type'
import { TrainingSessionGroup } from '../../types/training-session-group.type'
import { TrainingSession, TrainingSessionStep } from '../../types/training-session.type'
import { calculateNextTimeSlot, validateTrainingSession } from '../../utils/trainingSessionValidation'
import { StyledButton } from '../admin/Login'
import { AdminForm } from '../admin/users/AdminUsersForm'

interface CoachTrainingSessionFormModalProps {
    isOpen: boolean
    onClose: () => void
    session: TrainingSession | null
    refetchGroups: () => void
}

const CoachTrainingSessionFormModal: React.FC<CoachTrainingSessionFormModalProps> = ({
    isOpen,
    onClose,
    session,
    refetchGroups,
}) => {
    const { t } = useTranslation()
    const { formData, setFormData, resetForm } = useTrainingSessionForm(session)
    const userContext = useContext(UserContext)
    const user = userContext
    const queryClient = useQueryClient()

    // Récupération des groupes de sessions (owned + shared)
    const { data: groups = [] } = useQuery<TrainingSessionGroup[]>({
        queryKey: ['trainingSessionGroups'],
        queryFn: async (): Promise<TrainingSessionGroup[]> => {
            if (!user) throw new Error('User not authenticated')

            // Récupérer les groupes owned et shared en parallèle
            const [ownedResponse, sharedResponse] = await Promise.all([
                apiClient.get<TrainingSessionGroup[]>('/training-session-groups?type=owned'),
                apiClient.get<TrainingSessionGroup[]>('/training-session-groups?type=shared'),
            ])

            // Vérifier les erreurs pour les deux requêtes
            if (ownedResponse.status >= 400) {
                const errorData = ownedResponse.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to fetch owned training session groups')
            }

            if (sharedResponse.status >= 400) {
                const errorData = sharedResponse.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to fetch shared training session groups')
            }

            // Fusionner les deux tableaux et éliminer les doublons par ID
            const ownedGroups = ownedResponse.data
            const sharedGroups = sharedResponse.data

            // Créer une Map pour éviter les doublons
            const groupsMap = new Map<number, TrainingSessionGroup>()

            // Ajouter les groupes owned
            ownedGroups.forEach((group) => {
                groupsMap.set(group.id, group)
            })

            // Ajouter les groupes shared (s'ils ne sont pas déjà présents)
            sharedGroups.forEach((group) => {
                if (!groupsMap.has(group.id)) {
                    groupsMap.set(group.id, group)
                }
            })

            // Retourner le tableau fusionné
            return Array.from(groupsMap.values())
        },
        enabled: !!user,
    })

    // Récupération des exercices
    const { data: exercises = [] } = useQuery<Exercise[]>({
        queryKey: ['exercises'],
        queryFn: async (): Promise<Exercise[]> => {
            if (!user) throw new Error('User not authenticated')
            const response = await apiClient.get<Exercise[]>('/exercises')

            if (response.status >= 400) {
                const errorData = response.data as { message?: string }
                throw new Error(errorData?.message ?? 'Failed to fetch exercises')
            }

            return response.data
        },
        enabled: !!user,
    })

    // Hook pour les mutations
    const { submitTrainingSession, isLoading: isMutationLoading } = useTrainingSessionMutations({
        user,
        onSuccess: () => {
            refetchGroups()
            onClose()
        },
        onSettled: () => {
            // Mutation terminée
        },
    })

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleDescriptionChange = (value: string) => {
        setFormData((prev) => ({
            ...prev,
            description: value || '',
        }))
    }

    const handleStepChange = <K extends keyof TrainingSessionStep>(
        index: number,
        field: K,
        value: string | number | undefined,
    ) => {
        setFormData((prev) => {
            const newSteps = prev.steps.map((step, i) => {
                if (i === index) {
                    const updatedStep = {
                        ...step,
                        [field]:
                            field === 'exerciseId'
                                ? value !== undefined && value !== ''
                                    ? Number(value)
                                    : undefined
                                : value,
                    }
                    return updatedStep
                }
                return step
            })

            // Mise à jour automatique des temps si nécessaire
            if (field === 'startTime' || field === 'endTime') {
                const timeValue = value as string

                if (field === 'startTime' && index > 0) {
                    newSteps[index - 1] = {
                        ...newSteps[index - 1],
                        endTime: timeValue,
                    }
                }

                if (field === 'endTime' && index < newSteps.length - 1) {
                    newSteps[index + 1] = {
                        ...newSteps[index + 1],
                        startTime: timeValue,
                    }
                }
            }

            return { ...prev, steps: newSteps }
        })
    }

    const addStep = () => {
        const lastStep = formData.steps[formData.steps.length - 1]
        const lastEndTime = lastStep ? lastStep.endTime : '09:00'

        const { startTime, endTime } = calculateNextTimeSlot(lastEndTime)

        setFormData((prev) => ({
            ...prev,
            steps: [
                ...prev.steps,
                {
                    title: 'Nouvelle étape',
                    description: '',
                    startTime,
                    endTime,
                    exerciseId: undefined,
                },
            ],
        }))
    }

    const removeStep = (index: number) => {
        if (formData.steps.length <= 1) return
        setFormData((prev) => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index),
        }))
    }

    const handleMoveStep = (currentIndex: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

        if (newIndex < 0 || newIndex >= formData.steps.length) return

        setFormData((prev) => {
            const newSteps = [...prev.steps]

            // Échanger uniquement les contenus (sans les temps)
            const targetContent = {
                title: prev.steps[newIndex].title,
                description: prev.steps[newIndex].description,
                exerciseId: prev.steps[newIndex].exerciseId,
            }

            const currentContent = {
                title: prev.steps[currentIndex].title,
                description: prev.steps[currentIndex].description,
                exerciseId: prev.steps[currentIndex].exerciseId,
            }

            newSteps[currentIndex] = {
                ...newSteps[currentIndex],
                ...targetContent,
            }

            newSteps[newIndex] = {
                ...newSteps[newIndex],
                ...currentContent,
            }

            return { ...prev, steps: newSteps }
        })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user) {
            toast.error(t('common.authRequired'))
            return
        }

        if (isMutationLoading) {
            return
        }

        // Validation avec les utilitaires
        const validation = validateTrainingSession(formData, t)
        if (!validation.isValid) {
            validation.errors.forEach((error) => {
                toast.error(error.message)
            })
            return
        }

        try {
            await submitTrainingSession(formData, session?.id)
        } catch (error) {
            console.error('Error submitting form:', error)
            const errorMessage = error instanceof Error ? error.message : t('common.errorOccurred')
            toast.error(errorMessage)
        }
    }

    // Ne pas rendre le formulaire tant que les données ne sont pas synchronisées
    const isDataReady = !session || (session && formData.id === session.id)

    return (
        <Modal open={isOpen} onClose={onClose}>
            <ModalContent>
                <Typography variant="h5" component="h2" gutterBottom>
                    {session ? t('coach.trainingSessions.editSession') : t('coach.trainingSessions.newSession')}
                </Typography>

                {isDataReady ? (
                    <StyledAdminForm onSubmit={handleSubmit}>
                        <TrainingSessionBasicFields
                            formData={formData}
                            groups={groups}
                            onInputChange={handleInputChange}
                            onDescriptionChange={handleDescriptionChange}
                        />

                        <TrainingSessionStepsList
                            formData={formData}
                            exercises={exercises}
                            onStepChange={handleStepChange}
                            onAddStep={addStep}
                            onRemoveStep={removeStep}
                            onMoveStep={handleMoveStep}
                        />

                        <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <StyledButton type="button" variant="outline-secondary" onClick={onClose}>
                                {t('common.cancel')}
                            </StyledButton>
                            <StyledButton type="submit" variant="primary" disabled={isMutationLoading}>
                                {isMutationLoading ? '...' : session ? t('common.update') : t('common.create')}
                            </StyledButton>
                        </div>
                    </StyledAdminForm>
                ) : (
                    <div style={{ padding: '20px', textAlign: 'center' }}>{t('common.loading')}...</div>
                )}
            </ModalContent>
        </Modal>
    )
}

const StyledAdminForm = styled(AdminForm)`
    margin-top: 20px;
`

const ModalContent = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90%;
    max-width: 900px;
    max-height: 90vh;
    background-color: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    padding: 24px;
    border-radius: 8px;
    overflow-y: auto;
`

export default CoachTrainingSessionFormModal
